import { FC, useMemo } from 'react';

import { FileTypes, MediaFile } from '@/api/shared.types.ts';
import useKeyHandler from '@/hooks/global/useKeyHandler.ts';

const CloseButton: FC<{ onClose: () => void }> = ({ onClose }) => (
  <button
    className="absolute top-3 right-6 text-white text-4xl font-bold z-50"
    onClick={onClose}
  >
    ×
  </button>
);

const PrevButton: FC<{ onPrev: () => void }> = ({ onPrev }) => (
  <button
    className="fixed left-4 top-1/2 -translate-y-1/2 text-white text-5xl z-50 hover:scale-110 transition"
    onClick={(e) => {
      e.stopPropagation();
      onPrev();
    }}
  >
    ‹
  </button>
);

const NextButton: FC<{ onNext: () => void }> = ({ onNext }) => (
  <button
    className="fixed right-4 top-1/2 -translate-y-1/2 text-white text-5xl z-50 hover:scale-110 transition"
    onClick={(e) => {
      e.stopPropagation();
      onNext();
    }}
  >
    ›
  </button>
);

type FullScreenViewerProps = {
  files: MediaFile[];
  currentIndex: number | null;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  isOpen: boolean;
};

const FullScreenMediaViewer: FC<FullScreenViewerProps> = ({
  files,
  currentIndex,
  onClose,
  onNext,
  onPrev,
  isOpen,
}) => {
  useKeyHandler(isOpen, {
    Escape: onClose,
    ArrowRight: onNext,
    ArrowLeft: onPrev,
  });

  const isMobile = useMemo(
    () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
    []
  );

  const file = useMemo(() => {
    if (!files || currentIndex >= files.length) {
      return null;
    }
    return files[currentIndex];
  }, [files, currentIndex]);

  if (!isOpen || !file) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 w-screen h-screen flex items-center justify-center bg-black/90"
      style={{ margin: 0 }}
      onClick={onClose}
    >
      <CloseButton onClose={onClose} />

      {files.length > 1 && (
        <>
          <PrevButton onPrev={onPrev} />
          <NextButton onNext={onNext} />
        </>
      )}

      <div className="flex items-center justify-center w-[90%] h-[90%] max-w-[90vw] max-h-[90vh]">
        {file.type === FileTypes.IMAGE && (
          <img
            src={file.url}
            alt={file.name || 'image'}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {file.type === FileTypes.VIDEO && (
          <video
            src={file.url}
            controls
            autoPlay
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {file.type === FileTypes.PDF &&
          (isMobile ? (
            <div
              className="bg-white rounded-lg p-6 max-w-md text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-2">
                PDF preview not available
              </h3>
              <p className="text-gray-600 mb-4">
                Download the file to view it in your preferred app.
              </p>
              <a
                href={file.url}
                download={file.name || 'document.pdf'}
                className="inline-block bg-honoursOrange text-white px-4 py-2 rounded-lg"
              >
                Download PDF
              </a>
            </div>
          ) : (
            <div className="w-full h-full overflow-auto bg-white flex items-center justify-center">
              <iframe
                src={file.url}
                title={file.name || 'pdf'}
                className="w-full h-full min-h-[500px]"
                style={{ padding: 0, margin: 0, border: 'none' }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ))}
      </div>
    </div>
  );
};

export default FullScreenMediaViewer;
